import { ArrowRight, LifeBuoy, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MarketingPage } from '@/modules/marketing/MarketingPage';
import { usePageMeta } from '@/lib/seo';
import { PAGES } from '@/seo/pages';

/**
 * Contact.
 *
 * ## Two routes, no form
 *
 * There is no public contact endpoint and transactional email is not configured, so a form would
 * take a name, an address and a message and quietly discard all three behind a "thanks, we'll be in
 * touch". Somebody with a real problem would believe they had reported it. Both cards here point at
 * something that genuinely reaches a person.
 *
 * ## SUPPORT_EMAIL must be a mailbox somebody reads
 *
 * It is the only address on the page. If it bounces, the page is decorative.
 */
const SUPPORT_EMAIL = 'support@techiegain.com';

export function ContactPage() {
  usePageMeta(PAGES.contact);

  return (
    <MarketingPage
      eyebrow="Contact"
      title="Contact Us"
      standfirst="Questions about the platform, pricing or your requirements? Email us, or open a ticket from your dashboard if you are already a customer."
      wide
    >
      <div className="mx-auto mt-14 grid max-w-4xl gap-5 md:grid-cols-2">
        <div className="border-border/60 flex flex-col rounded-xl border p-7">
          <span className="bg-primary/10 text-primary inline-flex size-10 items-center justify-center rounded-xl">
            <LifeBuoy className="size-5" aria-hidden />
          </span>
          <h2 className="mt-5 font-medium tracking-tight">Existing customers</h2>
          <p className="text-muted-foreground mt-3 flex-1 text-sm leading-relaxed">
            Open a support ticket from your dashboard. The whole conversation stays with your account.
          </p>
          <Link
            to="/login"
            className="text-primary mt-6 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
          >
            Log in to open a ticket
            <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>

        <div className="border-border/60 flex flex-col rounded-xl border p-7">
          <span className="bg-primary/10 text-primary inline-flex size-10 items-center justify-center rounded-xl">
            <Mail className="size-5" aria-hidden />
          </span>
          <h2 className="mt-5 font-medium tracking-tight">Before you sign up</h2>
          <p className="text-muted-foreground mt-3 flex-1 text-sm leading-relaxed">
            Ask about features, pricing, or whether the platform suits your servers and team.
          </p>
          <p className="text-foreground/90 mt-4 text-sm">{SUPPORT_EMAIL}</p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-primary mt-3 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
          >
            Email us
            <ArrowRight className="size-4" aria-hidden />
          </a>
        </div>
      </div>

      <section className="mx-auto mt-20 max-w-3xl">
        <div className="border-border/60 from-primary/10 rounded-2xl border bg-gradient-to-b to-transparent p-10 text-center">
          <h2 className="text-xl font-semibold tracking-tight text-balance sm:text-2xl">
            Ready to get started?
          </h2>
          <p className="text-muted-foreground mx-auto mt-3 max-w-md">
            Start a free trial, connect your first server and run a playbook with preflight checks and a
            canary server.
          </p>
          <Link
            to="/register"
            className="bg-primary text-primary-foreground mt-8 inline-flex items-center rounded-full px-8 py-3.5 font-medium transition-opacity hover:opacity-90"
          >
            Start free trial
          </Link>
        </div>
      </section>
    </MarketingPage>
  );
}
